(function () {
  'use strict';

  const VIEW = 'orders';
  const FILTERS = Object.freeze(['all', 'in_transit', 'delivered', 'cancelled']);
  const COUNT_FILTERS = FILTERS;
  const DEFAULT_PAGE_SIZE = 3;
  const APPROVED_EXTERNAL_ORIGINS = Object.freeze({
    pcApprovedTrackingOrigins: Object.freeze([
      'https://shopify.17track.net',
      'https://www.dhl.com',
      'https://www.estafeta.com'
    ])
  });
  const states = new WeakMap();

  const asText = (value) => value == null ? '' : String(value);
  const asArray = (value) => Array.isArray(value) ? value : [];
  const slug = (value) => asText(value).trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || 'unknown';
  const isAbort = (error) => error?.name === 'AbortError' || error?.code === 'ABORTED';

  function abortError() {
    try {
      return new DOMException('Orders request superseded by a later intent.', 'AbortError');
    } catch (_) {
      const error = new Error('Orders request superseded by a later intent.');
      error.name = 'AbortError';
      return error;
    }
  }

  function stateFor(root) {
    let state = states.get(root);
    if (!state) {
      state = {
        intent: 0,
        status: 'all',
        pageSize: DEFAULT_PAGE_SIZE,
        cursorHistory: [null],
        cursorIndex: 0,
        pageInfo: null,
        focusPagination: false,
        searchEnabled: false,
        search: '',
        summary: null,
        summaryCounts: null,
        listCounts: null,
        counts: null,
        summaryPartial: false,
        countSource: '',
        searchInput: null,
        searchListeners: null,
        shell: null
      };
      states.set(root, state);
    }
    return state;
  }

  function ordersRoot(root) {
    if (root?.matches?.('.pc-orders')) return root;
    return root?.querySelector?.('[data-pc-view="orders"] .pc-orders, .pc-orders') || null;
  }

  function searchEnabledFor(root) {
    return ordersRoot(root)?.dataset?.pcOrdersSearchEnabled === 'true';
  }

  function orderDetailEnabledFor(root) {
    const shell = root?.closest?.('[data-personal-center]') || root?.querySelector?.('[data-personal-center]');
    return shell?.dataset?.pcOrderDetailEnabled === 'true';
  }

  function panelFor(root) {
    if (root?.matches?.('[data-pc-view="orders"]')) return root;
    return root?.querySelector?.('[data-pc-view="orders"]') || ordersRoot(root)?.closest('[data-pc-view="orders"]') || null;
  }

  function pageSizeFor(root, value) {
    const parsed = Number(value ?? root?.dataset?.pcOrdersPageSize ?? DEFAULT_PAGE_SIZE);
    return Number.isInteger(parsed) && parsed >= 1 && parsed <= 50 ? parsed : DEFAULT_PAGE_SIZE;
  }

  function filterFor(value) {
    return FILTERS.includes(value) ? value : 'all';
  }

  function searchFor(value) {
    return asText(value).trim();
  }

  function searchFromLocation() {
    try {
      return searchFor(new URL(window.location.href).searchParams.get('search'));
    } catch (_) {
      return '';
    }
  }

  function hasOwn(value, key) {
    return Boolean(value) && Object.prototype.hasOwnProperty.call(value, key);
  }

  function resetCursor(state) {
    state.cursorHistory = [null];
    state.cursorIndex = 0;
    state.pageInfo = null;
  }

  function queryParams(state, cursor = null) {
    const params = { status: state.status, pageSize: state.pageSize, cursor };
    if (state.searchEnabled && state.search) params.search = state.search;
    return params;
  }

  function syncSearchHistory(state) {
    if (!window.history?.pushState) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('pc_view', VIEW);
      url.searchParams.set('status', state.status);
      if (state.searchEnabled && state.search) url.searchParams.set('search', state.search);
      else url.searchParams.delete('search');
      const historyState = Object.assign({}, window.history.state || {}, {
        personalCenterView: VIEW,
        personalCenterFilter: state.status,
        personalCenterParams: queryParams(state)
      });
      window.history.pushState(historyState, '', `${url.pathname}${url.search}${url.hash}`);
    } catch (_) {
      // URL history is an enhancement; the reload params remain authoritative.
    }
  }

  function updateSearch(state, value) {
    const next = searchFor(value);
    const requiresReset = state.cursorIndex !== 0 || state.cursorHistory.length !== 1 || state.cursorHistory[0] !== null;
    if (next === state.search && !requiresReset) return false;
    state.search = next;
    resetCursor(state);
    return true;
  }

  function completeCounts(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const counts = {};
    for (const filter of COUNT_FILTERS) {
      const numeric = Number(value[filter]);
      if (!Number.isFinite(numeric) || numeric < 0) return null;
      counts[filter] = numeric;
    }
    return counts;
  }

  function countsFromSummary(summary) {
    const data = summary?.data;
    return completeCounts(data?.counts) || completeCounts(data?.orderCounts) || completeCounts(data);
  }

  function countsFromList(response) {
    return completeCounts(response?.meta?.counts);
  }

  function approvedOrigins(root, datasetKey) {
    const values = [
      ...(APPROVED_EXTERNAL_ORIGINS[datasetKey] || []),
      ...asText(root?.dataset?.[datasetKey]).split(',').map((value) => value.trim()).filter(Boolean)
    ];
    const origins = new Set([window.location.origin]);
    values.forEach((value) => {
      try {
        const url = new URL(value, window.location.origin);
        if (url.protocol === 'https:') origins.add(url.origin);
      } catch (_) {
        // Malformed merchant data is not an approval.
      }
    });
    return origins;
  }

  function safeApprovedUrl(root, rawValue, datasetKey) {
    const raw = asText(rawValue).trim();
    if (!raw) return '';
    try {
      const url = new URL(raw, window.location.origin);
      const sameOrigin = url.origin === window.location.origin;
      const externallySecure = url.protocol === 'https:';
      return (sameOrigin || externallySecure) && approvedOrigins(root, datasetKey).has(url.origin) ? url.href : '';
    } catch (_) {
      return '';
    }
  }

  function textFrom(root, selector, fallback) {
    return asText(root?.querySelector?.(selector)?.textContent).trim() || fallback;
  }

  function labelsFor(root) {
    return {
      total: textFrom(root, '[data-pc-orders-label="total"]', 'Total'),
      track: textFrom(root, '[data-pc-orders-label="track"]', 'Rastrear'),
      view: textFrom(root, '[data-pc-orders-label="view"]', 'Ver pedido'),
      reorder: textFrom(root, '[data-pc-orders-label="reorder"]', 'Volver a pedir'),
      partial: textFrom(root, '[data-pc-orders-label="partial"]', 'Algunos datos del pedido no están disponibles.'),
      statusInTransit: textFrom(root, '[data-pc-orders-label="status-in-transit"]', 'En camino'),
      statusDelivered: textFrom(root, '[data-pc-orders-label="status-delivered"]', 'Entregado'),
      statusCancelled: textFrom(root, '[data-pc-orders-label="status-cancelled"]', 'Cancelado'),
      statusPending: textFrom(root, '[data-pc-orders-label="status-pending"]', 'Actualización pendiente'),
      statusUnknown: textFrom(root, '[data-pc-orders-label="status-unknown"]', 'Estado de envío no disponible'),
      stageConfirmed: textFrom(root, '[data-pc-orders-label="stage-confirmed"]', 'Confirmado'),
      stageInTransit: textFrom(root, '[data-pc-orders-label="stage-in-transit"]', 'En tránsito'),
      stageOutForDelivery: textFrom(root, '[data-pc-orders-label="stage-out-for-delivery"]', 'En reparto'),
      stageDelivered: textFrom(root, '[data-pc-orders-label="stage-delivered"]', 'Entregado'),
      reorderUnavailable: asText(root?.closest?.('[data-personal-center]')?.dataset?.pcReorderUnavailable).trim() || 'Esta función no está disponible.'
    };
  }

  function localizedStatus(rawStatus, labels) {
    switch (asText(rawStatus).trim().toLowerCase()) {
      case 'in_transit': return labels.statusInTransit;
      case 'delivered': return labels.statusDelivered;
      case 'cancelled':
      case 'canceled': return labels.statusCancelled;
      case 'pending': return labels.statusPending;
      default: return labels.statusUnknown;
    }
  }

  function fulfillmentStatusFor(fulfillment, order) {
    return asText(
      fulfillment?.displayStatus
      || order?.status
      || fulfillment?.status
      || fulfillment?.fulfillmentStatus
      || order?.fulfillmentStatus
    ).trim();
  }

  function localizedTimelineKey(rawKey, labels) {
    switch (asText(rawKey).trim().toLowerCase()) {
      case 'confirmed': return labels.stageConfirmed;
      case 'in_transit': return labels.stageInTransit;
      case 'out_for_delivery': return labels.stageOutForDelivery;
      case 'delivered': return labels.stageDelivered;
      default: return labels.statusUnknown;
    }
  }

  function fallbackMedia(media) {
    const namespace = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(namespace, 'svg');
    const rect = document.createElementNS(namespace, 'rect');
    const path = document.createElementNS(namespace, 'path');
    svg.classList.add('pc-orders__product-placeholder');
    svg.setAttribute('viewBox', '0 0 320 240');
    svg.setAttribute('width', '320');
    svg.setAttribute('height', '240');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('aria-hidden', 'true');
    rect.setAttribute('width', '320');
    rect.setAttribute('height', '240');
    rect.setAttribute('fill', '#f1f4f8');
    path.setAttribute('d', 'M74 161 123 112l35 35 25-25 63 63H74Zm34-58a17 17 0 1 0 0-34 17 17 0 0 0 0 34Z');
    path.setAttribute('fill', '#94a3b8');
    svg.append(rect, path);
    media.replaceChildren(svg);
    media.dataset.pcImageState = 'fallback';
    media.setAttribute('aria-hidden', 'true');
  }

  function renderMedia(root, media, lineItem) {
    const image = lineItem?.image;
    const width = Number(image?.width);
    const height = Number(image?.height);
    const source = safeApprovedUrl(root, image?.url, 'pcApprovedMediaOrigins');
    if (!source || !Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
      fallbackMedia(media);
      return;
    }

    const imageNode = document.createElement('img');
    imageNode.className = 'pc-orders__product-image';
    imageNode.src = source;
    imageNode.width = width;
    imageNode.height = height;
    imageNode.loading = 'lazy';
    imageNode.decoding = 'async';
    imageNode.alt = asText(image?.alt || lineItem?.title || lineItem?.sku).trim();
    imageNode.addEventListener('error', () => fallbackMedia(media), { once: true });
    media.replaceChildren(imageNode);
    media.dataset.pcImageState = 'image';
    media.removeAttribute('aria-hidden');
  }

  function formatDate(value) {
    const raw = asText(value).trim();
    if (!raw) return '';
    const timestamp = Date.parse(raw);
    if (Number.isNaN(timestamp)) return raw;
    // Store timezone, not the device's: an order placed late at night in Mexico would
    // otherwise be dated a day later for anyone browsing from Asia.
    return new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeZone: 'America/Mexico_City' })
      .format(new Date(timestamp));
  }

  function formatMoney(total, currencyCode) {
    const amount = typeof total === 'object' && total !== null ? total.amount : total;
    const currency = asText(currencyCode || total?.currencyCode).trim();
    const numeric = Number(amount);
    if (!Number.isFinite(numeric) || !currency) return asText(amount).trim();
    try {
      const hasFraction = Math.abs(numeric % 1) > Number.EPSILON;
      const formatted = new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: hasFraction ? 2 : 0,
        maximumFractionDigits: hasFraction ? 2 : 0
      }).format(numeric);
      return `${formatted} ${currency}`;
    } catch (_) {
      return `${numeric} ${currency}`;
    }
  }

  function lineItemSubtotal(lineItem, currencyCode) {
    const total = lineItem?.lineTotal;
    const amount = typeof total === 'object' && total !== null ? total.amount : total;
    const currency = asText(total?.currencyCode || lineItem?.currencyCode || currencyCode).trim();
    if (total == null || total === '' || !currency || !Number.isFinite(Number(amount))) return '';
    return formatMoney(total, currency);
  }

  function replaceText(node, value) {
    if (node) node.textContent = asText(value);
  }

  function makeElement(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = asText(text);
    return node;
  }

  function statusIcon(rawStatus) {
    const namespace = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(namespace, 'svg');
    const status = asText(rawStatus).trim().toLowerCase();
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    if (status === 'delivered') {
      const circle = document.createElementNS(namespace, 'circle');
      const path = document.createElementNS(namespace, 'path');
      circle.setAttribute('cx', '12');
      circle.setAttribute('cy', '12');
      circle.setAttribute('r', '9');
      circle.setAttribute('fill', 'none');
      circle.setAttribute('stroke', 'currentColor');
      circle.setAttribute('stroke-width', '1.8');
      path.setAttribute('d', 'm8 12 2.5 2.5L16 9');
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', 'currentColor');
      path.setAttribute('stroke-width', '1.8');
      path.setAttribute('stroke-linecap', 'round');
      path.setAttribute('stroke-linejoin', 'round');
      svg.append(circle, path);
      return svg;
    }
    const path = document.createElementNS(namespace, 'path');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.8');
    if (status === 'in_transit') {
      path.setAttribute('d', 'M3 6h11v10H3zm11 4h4l3 3v3h-7zM7 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm10 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z');
      path.setAttribute('stroke-linejoin', 'round');
    } else {
      path.setAttribute('d', 'M12 3v18M3 12h18');
      path.setAttribute('stroke-linecap', 'round');
    }
    svg.appendChild(path);
    return svg;
  }

  function renderStatus(card, order, labels) {
    const status = card.querySelector('[data-pc-status]');
    if (!status) return;
    const rawStatus = asText(order?.status || order?.fulfillmentStatus).trim().toLowerCase() || 'unknown';
    const label = makeElement('span', '', localizedStatus(rawStatus, labels));
    label.dataset.pcOrderStatusLabel = '';
    status.className = `pc-orders__status pc-orders__status--${slug(rawStatus)}`;
    status.dataset.pcStatus = rawStatus;
    status.replaceChildren(statusIcon(rawStatus), label);
  }

  function renderLineItems(card, order) {
    const list = card.querySelector('[data-pc-order-line-items]');
    const summary = card.querySelector('[data-pc-order-items-summary]');
    const lineItems = asArray(order?.lineItems);
    card.dataset.pcOrderItemCount = asText(order?.itemCount);
    if (!list || !summary) return;
    list.replaceChildren();
    lineItems.forEach((lineItem) => {
      const item = makeElement('li', 'pc-orders__line-item');
      const title = makeElement('span', 'pc-orders__line-item-title', lineItem?.title || lineItem?.sku || '');
      const quantity = makeElement('span', 'pc-orders__line-item-quantity', lineItem?.quantity == null ? '' : `× ${lineItem.quantity}`);
      item.dataset.pcLineItemId = asText(lineItem?.id);
      item.dataset.pcLineItemSku = asText(lineItem?.sku);
      item.append(title, quantity);
      list.appendChild(item);
    });
    list.hidden = true;
    summary.textContent = lineItems.map((lineItem) => {
        const title = asText(lineItem?.title || lineItem?.sku).trim();
        const quantity = lineItem?.quantity == null ? '' : ` × ${lineItem.quantity}`;
        const subtotal = lineItemSubtotal(lineItem, order?.currencyCode);
        return [`${title}${quantity}`.trim(), subtotal].filter(Boolean).join(' · ');
      }).filter(Boolean).join(' · ');
    summary.hidden = summary.textContent.trim() === '';
  }

  function shouldHideTimeline(order) {
    const statuses = [order?.status, order?.fulfillmentStatus]
      .map((status) => asText(status).trim().toLowerCase());
    return statuses.includes('cancelled') || statuses.includes('canceled') || Boolean(asText(order?.cancelledAt).trim());
  }

  function renderTimeline(card, order, labels) {
    const list = card.querySelector('[data-pc-order-timeline]');
    if (!list) return;
    list.replaceChildren();
    const hidden = shouldHideTimeline(order);
    list.hidden = hidden;
    list.setAttribute('aria-hidden', String(hidden));
    if (hidden) {
      delete list.dataset.pcProgressStage;
      delete list.dataset.pcCurrentMilestone;
      return;
    }
    const entries = asArray(order?.timeline);
    const rawStatus = asText(order?.status || order?.fulfillmentStatus).trim().toLowerCase();
    let currentIndex = entries.findIndex((entry) => entry?.current === true || asText(entry?.key).trim().toLowerCase() === rawStatus);
    if (currentIndex < 0 && rawStatus === 'delivered') currentIndex = entries.findIndex((entry) => asText(entry?.key).trim().toLowerCase() === 'delivered');
    if (currentIndex < 0) currentIndex = entries.findIndex((entry) => entry?.done !== true);
    if (currentIndex < 0 && entries.length > 0) currentIndex = entries.length - 1;
    list.dataset.pcProgressStage = rawStatus || 'unknown';
    if (currentIndex >= 0) list.dataset.pcCurrentMilestone = asText(entries[currentIndex]?.key);
    else delete list.dataset.pcCurrentMilestone;
    entries.forEach((entry, index) => {
      const item = makeElement('li', 'pc-orders__milestone');
      const done = entry?.done === true;
      const current = index === currentIndex;
      if (done) item.classList.add('is-complete');
      if (current) {
        item.classList.add('is-current');
        item.setAttribute('aria-current', 'step');
      }
      item.dataset.pcMilestoneKey = asText(entry?.key);
      item.dataset.pcMilestoneStage = asText(entry?.key);
      const dot = makeElement('span', 'pc-orders__milestone-dot', done ? '✓' : '');
      const key = makeElement('span', '', localizedTimelineKey(entry?.key, labels));
      const time = makeElement('time', '', formatDate(entry?.at || entry?.estimatedAt));
      if (asText(entry?.at).trim()) time.dateTime = asText(entry.at).trim();
      item.append(dot, key, time);
      list.appendChild(item);
    });
  }

  function appendTracking(root, list, tracking, labels) {
    const item = makeElement('li', 'pc-orders__tracking-item');
    const company = makeElement('span', 'pc-orders__tracking-company', tracking?.company);
    const number = makeElement('span', 'pc-orders__tracking-number', tracking?.number);
    const url = safeApprovedUrl(root, tracking?.trackingUrl || tracking?.url, 'pcApprovedTrackingOrigins');
    item.append(company, number);
    if (url) {
      const link = makeElement('a', 'pc-orders__tracking-link', labels.track);
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      item.appendChild(link);
    }
    list.appendChild(item);
  }

  function renderFulfillments(root, card, order, labels) {
    const container = card.querySelector('[data-pc-order-fulfillments]');
    const summary = card.querySelector('[data-pc-order-fulfillments-summary]');
    const fulfillments = asArray(order?.fulfillments);
    card.dataset.pcOrderFulfillmentCount = String(fulfillments.length);
    if (!container || !summary) return;
    container.replaceChildren();
    const renderedFulfillments = fulfillments.length > 0 ? fulfillments : [null];
    renderedFulfillments.forEach((fulfillment) => {
      const section = makeElement('section', 'pc-orders__fulfillment');
      const rawStatus = fulfillmentStatusFor(fulfillment, order);
      const status = makeElement('p', `pc-orders__fulfillment-status pc-orders__fulfillment-status--${slug(rawStatus)}`, localizedStatus(rawStatus, labels));
      const trackingList = makeElement('ul', 'pc-orders__tracking-list');
      section.dataset.pcFulfillmentId = asText(fulfillment?.id);
      section.dataset.pcFulfillmentStatus = rawStatus;
      status.dataset.pcFulfillmentStatusLabel = rawStatus;
      asArray(fulfillment?.tracking).forEach((tracking) => appendTracking(root, trackingList, tracking, labels));
      trackingList.hidden = trackingList.childElementCount === 0;
      section.append(status, trackingList);
      container.appendChild(section);
    });
    container.hidden = true;
    summary.textContent = '';
    summary.hidden = true;
  }

  function appendStatusPage(root, actions, order, labels) {
    const url = safeApprovedUrl(root, order?.statusPageUrl, 'pcApprovedOrderStatusOrigins');
    if (!url) return;
    const link = makeElement('a', 'pc-orders__action', labels.track);
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.dataset.pcAction = 'view-order';
    link.dataset.pcOrderId = asText(order?.id);
    actions.appendChild(link);
  }

  function appendOrderDetail(root, actions, order, labels) {
    const orderId = asText(order?.id).trim();
    if (!orderId) return;
    if (!orderDetailEnabledFor(root)) {
      const button = makeElement('button', 'pc-orders__action pc-orders__action--primary', labels.view);
      button.type = 'button';
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      actions.appendChild(button);
      return;
    }
    const link = makeElement('a', 'pc-orders__action pc-orders__action--primary', labels.view);
    const url = new URL(window.location.href);
    url.searchParams.set('pc_view', 'order-detail');
    url.searchParams.set('order_id', orderId);
    url.searchParams.delete('status');
    link.href = `${url.pathname}${url.search}${url.hash}`;
    link.dataset.pcAction = 'view-order-detail';
    link.dataset.pcOrderId = orderId;
    actions.appendChild(link);
  }

  function appendTopLevelTracking(root, actions, order, labels) {
    const url = safeApprovedUrl(root, order?.trackingUrl, 'pcApprovedTrackingOrigins');
    if (!url) return;
    const link = makeElement('a', 'pc-orders__action', labels.track);
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.dataset.pcAction = 'order-tracking';
    link.dataset.pcOrderId = asText(order?.id);
    actions.appendChild(link);
  }

  function renderActions(root, card, order, labels) {
    const actions = card.querySelector('[data-pc-order-actions]');
    if (!actions) return;
    actions.replaceChildren();
    appendOrderDetail(root, actions, order, labels);
    if (safeApprovedUrl(root, order?.statusPageUrl, 'pcApprovedOrderStatusOrigins')) appendStatusPage(root, actions, order, labels);
    else appendTopLevelTracking(root, actions, order, labels);
    actions.hidden = actions.childElementCount === 0;
  }

  function renderOrder(root, template, output, order, labels) {
    const card = template.content.firstElementChild.cloneNode(true);
    const lineItems = asArray(order?.lineItems);
    const rawStatus = asText(order?.status).trim();
    card.dataset.pcOrderId = asText(order?.id);
    card.dataset.pcOrderStatus = rawStatus;
    card.dataset.pcOrderFinancialStatus = asText(order?.financialStatus);
    card.dataset.pcOrderFulfillmentStatus = asText(order?.fulfillmentStatus);
    card.dataset.pcOrderCreatedAt = asText(order?.createdAt);
    card.dataset.pcOrderProcessedAt = asText(order?.processedAt);
    card.dataset.pcOrderCancelledAt = asText(order?.cancelledAt);
    card.dataset.pcOrderCancelReason = asText(order?.cancelReason);
    card.dataset.pcOrderDeliveredAt = asText(order?.deliveredAt);
    renderMedia(root, card.querySelector('[data-pc-media]'), lineItems[0]);
    replaceText(card.querySelector('[data-pc-order-name]'), order?.name || order?.id);
    const date = card.querySelector('[data-pc-order-date]');
    replaceText(date, formatDate(order?.createdAt));
    if (date && asText(order?.createdAt).trim()) date.dateTime = asText(order.createdAt).trim();
    replaceText(card.querySelector('[data-pc-order-total]'), formatMoney(order?.totals?.total, order?.currencyCode));
    renderLineItems(card, order);
    renderStatus(card, order, labels);
    renderTimeline(card, order, labels);
    renderFulfillments(root, card, order, labels);
    const summaries = card.querySelector('[data-pc-order-summaries]');
    if (summaries) {
      const hasItemSummary = asText(summaries.querySelector('[data-pc-order-items-summary]')?.textContent).trim() !== '';
      summaries.hidden = !hasItemSummary;
    }
    renderActions(root, card, order, labels);
    output.appendChild(card);
  }

  function renderPagination(root, state, response) {
    const pagination = root.querySelector('[data-pc-orders-pagination]');
    const previous = pagination?.querySelector('[data-pc-page-direction="previous"]');
    const status = pagination?.querySelector('[data-pc-orders-pagination-status]');
    const next = pagination?.querySelector('[data-pc-page-direction="next"]');
    const pageInfo = response?.meta?.pageInfo;
    state.pageInfo = pageInfo && typeof pageInfo === 'object' ? pageInfo : null;
    const canNext = pageInfo?.hasNextPage === true && asText(pageInfo?.nextCursor).trim() !== '';
    const canPrevious = state.cursorIndex > 0;
    if (!pagination || !previous || !status || !next) return;
    const isVisible = canNext || canPrevious;
    const pageLabel = asText(root.closest('[data-personal-center]')?.dataset.pcPaginationPage).trim() || 'Página';
    pagination.hidden = !isVisible;
    previous.disabled = !canPrevious;
    next.disabled = !canNext;
    previous.setAttribute('aria-disabled', String(!canPrevious));
    next.setAttribute('aria-disabled', String(!canNext));
    status.textContent = `${pageLabel} ${state.cursorIndex + 1}`;
    if (state.focusPagination) {
      state.focusPagination = false;
      if (isVisible) window.requestAnimationFrame(() => status.focus({ preventScroll: true }));
    }
  }

  function renderEmptyState(orders, status) {
    const output = orders.querySelector('[data-pc-orders-empty-output]');
    const selectedStatus = filterFor(status);
    const template = orders.querySelector(`[data-pc-orders-empty-template="${selectedStatus}"]`);
    if (!output || !template?.content) return;

    output.replaceChildren(template.content.cloneNode(true));
    output.dataset.pcOrdersEmptyStatus = selectedStatus;
  }

  function renderCounts(orders, state) {
    const counts = state.counts;
    orders.dataset.pcOrdersCounts = JSON.stringify(counts || null);
    orders.dataset.pcOrdersCountsSource = state.countSource || '';
    orders.querySelectorAll('[data-pc-orders-count]').forEach((output) => {
      const filter = filterFor(output.dataset.pcOrdersCount);
      const value = counts?.[filter];
      const visible = Number.isFinite(value);
      output.hidden = !visible;
      output.textContent = visible ? `(${value})` : '';
    });
  }

  function searchControls(orders) {
    return {
      input: orders?.querySelector?.('[data-pc-orders-search-input]') || null,
      clear: orders?.querySelector?.('[data-pc-action="clear-orders-search"]') || null
    };
  }

  function syncSearchControls(orders, state, { preserveInput = false } = {}) {
    const { input, clear } = searchControls(orders);
    if (!input) return;
    const active = typeof document === 'undefined' ? null : document.activeElement;
    if (!preserveInput && active !== input) input.value = state.search;
    const hasValue = searchFor(input.value) !== '';
    if (clear) {
      clear.hidden = !hasValue;
      clear.disabled = !hasValue;
      clear.setAttribute('aria-disabled', String(!hasValue));
    }
  }

  function clearSearchListeners(state) {
    if (state.searchInput && state.searchListeners) {
      state.searchInput.removeEventListener('input', state.searchListeners.input);
      state.searchInput.removeEventListener('keydown', state.searchListeners.keydown);
    }
    state.searchInput = null;
    state.searchListeners = null;
  }

  function bindSearch(orders, state, shell) {
    state.shell = shell || state.shell;
    state.searchEnabled = searchEnabledFor(orders);
    if (!state.searchEnabled) {
      clearSearchListeners(state);
      return;
    }
    const { input } = searchControls(orders);
    if (!input) return;
    if (state.searchInput === input && state.searchListeners) {
      syncSearchControls(orders, state, { preserveInput: true });
      return;
    }
    clearSearchListeners(state);
    const listeners = {
      input: () => syncSearchControls(orders, state, { preserveInput: true }),
      keydown: (event) => {
        if (event.key !== 'Escape') return;
        const typed = searchFor(input.value);
        if (!typed && !state.search) return;
        event.preventDefault();
        input.value = '';
        syncSearchControls(orders, state, { preserveInput: true });
        if (updateSearch(state, '')) {
          syncSearchHistory(state);
          state.shell?.reload?.(VIEW, queryParams(state));
        }
      }
    };
    input.addEventListener('input', listeners.input);
    input.addEventListener('keydown', listeners.keydown);
    state.searchInput = input;
    state.searchListeners = listeners;
    syncSearchControls(orders, state);
  }

  function scrollFilterIntoView(target) {
    if (typeof window === 'undefined' || !window.matchMedia?.('(width < 768px)').matches) return;
    const filters = target?.closest?.('.pc-orders__filters');
    const label = target?.nextElementSibling;
    if (!filters || !label?.matches?.('.pc-orders__filter')) return;
    window.requestAnimationFrame(() => {
      const filtersRect = filters.getBoundingClientRect();
      const labelRect = label.getBoundingClientRect();
      const maximum = Math.max(0, filters.scrollWidth - filters.clientWidth);
      const nextLeft = Math.min(maximum, Math.max(0, filters.scrollLeft + labelRect.left - filtersRect.left - (filters.clientWidth - labelRect.width) / 2));
      const behavior = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      if (typeof filters.scrollTo === 'function') filters.scrollTo({ left: nextLeft, behavior });
      else filters.scrollLeft = nextLeft;
    });
  }

  function scrollSelectedFilterIntoView(orders) {
    scrollFilterIntoView(orders?.querySelector?.('.pc-orders__filter-control:checked'));
  }

  async function load({ client, root, params = {}, signal } = {}) {
    const state = stateFor(root);
    const status = filterFor(params.status);
    const pageSize = pageSizeFor(root, params.pageSize);
    const searchEnabled = searchEnabledFor(root);
    const requestedSearch = searchEnabled ? (hasOwn(params, 'search') ? searchFor(params.search) : searchFromLocation()) : '';
    const queryChanged = status !== state.status || requestedSearch !== state.search;
    const cursor = queryChanged || params.cursor == null || params.cursor === '' ? null : asText(params.cursor);
    const intent = ++state.intent;
    state.status = status;
    state.pageSize = pageSize;
    state.searchEnabled = searchEnabled;
    state.search = requestedSearch;
    if (queryChanged) resetCursor(state);
    const knownIndex = state.cursorHistory.findIndex((knownCursor) => knownCursor === cursor);
    if (knownIndex >= 0) state.cursorIndex = knownIndex;
    else {
      state.cursorHistory = state.cursorHistory.slice(0, state.cursorIndex + 1);
      state.cursorHistory.push(cursor);
      state.cursorIndex = state.cursorHistory.length - 1;
    }
    const readSummary = !requestedSearch && typeof client?.getOrderSummary === 'function' && !state.summaryCounts;
    const [ordersResult, summaryResult] = await Promise.allSettled([
      client.listOrders({ status, pageSize, cursor, search: searchEnabled && requestedSearch ? requestedSearch : undefined, signal }),
      readSummary ? client.getOrderSummary({ signal }) : Promise.resolve(null)
    ]);
    if (intent !== state.intent || signal?.aborted) throw abortError();
    if (ordersResult.status === 'rejected') throw ordersResult.reason;
    if (summaryResult.status === 'rejected' && isAbort(summaryResult.reason)) throw summaryResult.reason;

    if (readSummary) {
      const counts = summaryResult.status === 'fulfilled' ? countsFromSummary(summaryResult.value) : null;
      state.summary = summaryResult.status === 'fulfilled' ? summaryResult.value : null;
      state.summaryCounts = counts;
      state.summaryPartial = !counts;
    } else {
      state.summaryPartial = false;
    }
    state.listCounts = countsFromList(ordersResult.value);
    if (requestedSearch) {
      // The list response is the only count authority for a narrowed search.
      // A cached, unfiltered summary would otherwise show stale totals.
      state.counts = state.listCounts;
      state.countSource = state.listCounts ? 'list-meta-search' : 'search-counts-unavailable';
    } else {
      // Only a failed or incomplete summary may fall back to the list envelope.
      state.counts = state.summaryCounts || state.listCounts;
      state.countSource = state.summaryCounts ? 'summary' : state.summaryPartial ? 'list-meta-fallback' : state.listCounts ? 'list-meta' : '';
    }
    return ordersResult.value;
  }

  function render({ root, response, shell } = {}) {
    const orders = ordersRoot(root);
    const panel = panelFor(root);
    const output = orders?.querySelector('[data-pc-orders-list]');
    const template = orders?.querySelector('[data-pc-order-card-template]');
    const partialNotice = orders?.querySelector('[data-pc-orders-partial-notice]');
    const state = stateFor(root);
    const data = asArray(response?.data);
    const pageInfo = response?.meta?.pageInfo;
    const isPartial = response?.meta?.partial === true || pageInfo?.scanTruncated === true || state.summaryPartial === true;
    if (!orders || !panel || !output || !template?.content?.firstElementChild) return { state: 'error' };

    orders.dataset.pcOrdersPageSize = asText(pageInfo?.pageSize || state.pageSize);
    orders.dataset.pcOrdersTotal = asText(pageInfo?.total);
    renderCounts(orders, state);
    bindSearch(orders, state, shell);
    scrollSelectedFilterIntoView(orders);
    if (!state.search) shell?.setMobileNavigationBadge?.('orders', state.counts?.in_transit);
    output.replaceChildren();
    const labels = labelsFor(orders);
    data.forEach((order) => renderOrder(orders, template, output, order, labels));
    if (partialNotice) {
      partialNotice.hidden = !isPartial;
      partialNotice.textContent = isPartial ? labels.partial : '';
    }
    renderPagination(orders, state, response);
    if (data.length === 0) renderEmptyState(orders, state.status);
    return { state: data.length === 0 ? 'empty' : 'ready', partial: isPartial, counts: state.counts, pageInfo, summary: state.summary };
  }

  function renderReadFailure({ root, params = {} } = {}) {
    const orders = ordersRoot(root);
    if (!orders) return;
    const state = stateFor(root);
    state.status = filterFor(params.status || state.status);
    state.pageInfo = null;
    state.focusPagination = false;
    renderEmptyState(orders, state.status);
    const pagination = orders.querySelector('[data-pc-orders-pagination]');
    const partialNotice = orders.querySelector('[data-pc-orders-partial-notice]');
    if (pagination) pagination.hidden = true;
    if (partialNotice) partialNotice.hidden = true;
  }

  function handleAction({ event, action, root } = {}) {
    const state = stateFor(root);
    state.searchEnabled = searchEnabledFor(root);
    const target = action || event?.target?.closest?.('[data-pc-action], [data-pc-filter]');
    if (!target) return null;
    if (target.matches('[data-pc-filter]')) {
      scrollFilterIntoView(target);
      const params = queryParams({ ...state, status: filterFor(target.value) });
      return { view: VIEW, params, preventDefault: true };
    }
    if (target.dataset.pcForm === 'orders-search') {
      event?.preventDefault();
      if (!state.searchEnabled) return { handled: true, preventDefault: true };
      const input = searchControls(ordersRoot(root)).input;
      if (!updateSearch(state, input?.value)) return { handled: true, preventDefault: true };
      syncSearchHistory(state);
      return { view: VIEW, params: queryParams(state), preventDefault: true };
    }
    if (target.dataset.pcAction === 'clear-orders-search') {
      event?.preventDefault();
      if (!state.searchEnabled) return { handled: true, preventDefault: true };
      const { input } = searchControls(ordersRoot(root));
      if (input) input.value = '';
      syncSearchControls(ordersRoot(root), state, { preserveInput: true });
      if (!updateSearch(state, '')) return { handled: true, preventDefault: true };
      syncSearchHistory(state);
      return { view: VIEW, params: queryParams(state), preventDefault: true };
    }
    if (target.dataset.pcAction === 'orders-page') {
      const direction = target.dataset.pcPageDirection;
      if (direction === 'next' && state.pageInfo?.hasNextPage === true && asText(state.pageInfo.nextCursor).trim()) {
        state.focusPagination = true;
        return { view: VIEW, params: queryParams(state, state.pageInfo.nextCursor) };
      }
      if (direction === 'previous' && state.cursorIndex > 0) {
        state.focusPagination = true;
        return { view: VIEW, params: queryParams(state, state.cursorHistory[state.cursorIndex - 1]) };
      }
      return { handled: true };
    }
    if (target.dataset.pcAction === 'view-order-detail') {
      if (!orderDetailEnabledFor(root)) {
        event?.preventDefault();
        return { handled: true, preventDefault: true };
      }
      const orderId = asText(target.dataset.pcOrderId).trim();
      if (!orderId) return { handled: true, unavailable: true, message: labelsFor(root).statusUnknown };
      return {
        navigate: true,
        view: 'order-detail',
        params: { orderId, returnStatus: state.status },
        history: 'push',
        focus: true
      };
    }
    return null;
  }

  function disconnect({ root } = {}) {
    const state = states.get(root);
    if (state) clearSearchListeners(state);
    states.delete(root);
  }

  window.PersonalCenterViewModules = window.PersonalCenterViewModules || {};
  window.PersonalCenterViewModules.orders = { load, render, renderReadFailure, handleAction, disconnect };
}());
